import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { take } from 'rxjs';
import { CounselorNav } from '../counselor-nav/counselor-nav';
import { AnalyticsService, AnalyticsOverview } from '../../../../services/analytics.service';

/**
 * Insights and Analytics dashboard for faculty/admins.
 * Displays platform-wide metrics as stat cards and charts.
 */
@Component({
  selector: 'app-insights',
  standalone: true,
  imports: [CommonModule, CounselorNav, BaseChartDirective],
  templateUrl: './insights.html',
  styleUrl: './insights.css',
})
export class Insights implements OnInit {
  private analyticsService = inject(AnalyticsService);
  private cdr = inject(ChangeDetectorRef);

  /** The raw analytics payload from the server. */
  data: AnalyticsOverview | null = null;

  /** Whether the analytics data is loading. */
  loading = true;

  /** Whether the request failed. */
  error = false;

  // ── Chart configs ──────────────────────────────────────────────

  /** Shared palette drawn from the Handle brand colors. */
  private palette = ['#1e468b', '#daf74d', '#4a90d9', '#f5a623', '#7ed321', '#c0392b', '#9b59b6'];

  usersChartType: ChartType = 'doughnut';
  usersChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };

  contentChartType: ChartType = 'bar';
  contentChartData: ChartData<'bar'> = { labels: [], datasets: [] };

  completionChartType: ChartType = 'bar';
  completionChartData: ChartData<'bar'> = { labels: [], datasets: [] };

  connectionsChartType: ChartType = 'doughnut';
  connectionsChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };

  moderationChartType: ChartType = 'doughnut';
  moderationChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };

  reasonsChartType: ChartType = 'bar';
  reasonsChartData: ChartData<'bar'> = { labels: [], datasets: [] };

  featureChartType: ChartType = 'bar';
  featureChartData: ChartData<'bar'> = { labels: [], datasets: [] };

  /** Options for bar charts with a single dataset (hide legend). */
  barOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  /** Options for horizontal bar charts (feature usage). */
  horizontalBarOptions: ChartConfiguration['options'] = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { x: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  /** Options for doughnut charts. */
  doughnutOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' } },
  };

  ngOnInit(): void {
    this.loadAnalytics();
  }

  /**
   * Fetches the analytics overview and builds all chart datasets.
   * @returns {void}
   */
  loadAnalytics(): void {
    this.loading = true;
    this.error = false;
    this.analyticsService
      .getOverview()
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          this.data = data;
          this.buildCharts(data);
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Analytics API error:', err);
          this.error = true;
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Formats a moderation status/reason key into a readable label.
   * @param {string} value - The raw enum key.
   * @returns {string}
   */
  formatLabel(value: string): string {
    return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  /**
   * Populates all chart data structures from the analytics payload.
   * @param {AnalyticsOverview} d - The analytics data.
   * @returns {void}
   */
  private buildCharts(d: AnalyticsOverview): void {
    // Users by type
    this.usersChartData = {
      labels: ['Students', 'Staff', 'Administrators'],
      datasets: [
        {
          data: [d.users.students, d.users.staff, d.users.administrators],
          backgroundColor: [this.palette[0], this.palette[1], this.palette[2]],
        },
      ],
    };

    // Content activity
    this.contentChartData = {
      labels: ['Posts', 'Comments', 'Messages', 'Events'],
      datasets: [
        {
          label: 'Count',
          data: [d.content.posts, d.content.comments, d.content.messages, d.content.events],
          backgroundColor: this.palette[0],
          borderRadius: 6,
        },
      ],
    };

    // Profile completion buckets
    this.completionChartData = {
      labels: d.profileCompletion.buckets.map((b) => b.label),
      datasets: [
        {
          label: 'Students',
          data: d.profileCompletion.buckets.map((b) => b.count),
          backgroundColor: this.palette[4],
          borderRadius: 6,
        },
      ],
    };

    // Connections
    this.connectionsChartData = {
      labels: ['Accepted', 'Pending'],
      datasets: [
        {
          data: [d.connections.accepted, d.connections.pending],
          backgroundColor: [this.palette[4], this.palette[3]],
        },
      ],
    };

    // Moderation by status
    this.moderationChartData = {
      labels: d.moderation.byStatus.map((s) => this.formatLabel(s.status)),
      datasets: [
        {
          data: d.moderation.byStatus.map((s) => s.count),
          backgroundColor: [this.palette[3], this.palette[4], this.palette[5]],
        },
      ],
    };

    // Reports by reason
    this.reasonsChartData = {
      labels: d.moderation.byReason.map((r) => this.formatLabel(r.reason)),
      datasets: [
        {
          label: 'Reports',
          data: d.moderation.byReason.map((r) => r.count),
          backgroundColor: this.palette[5],
          borderRadius: 6,
        },
      ],
    };

    // Feature usage
    this.featureChartData = {
      labels: d.featureUsage.map((f) => f.feature),
      datasets: [
        {
          label: 'Requests',
          data: d.featureUsage.map((f) => f.count),
          backgroundColor: this.palette[2],
          borderRadius: 6,
        },
      ],
    };
  }
}
